import type { AssessmentResult, AssessmentSource, Verdict } from '../types.ts';

/**
 * Dziennik prób „powiedz zdanie” — dane do strojenia oceny na PRAWDZIWYM głosie (syntezator nie zastąpi dziecka ani dorosłego z akcentem).
 * Zapisuje się lokalnie w przeglądarce; w trybie testowym każdą próbę można opisać: „powiedziałem poprawnie” albo „celowo błędnie” (np. „fu” zamiast „food”).
 * Opisane próby rodzic kopiuje jednym przyciskiem i przekazuje do strojenia progów. Sam dziennik to tylko liczby i teksty z oceny, bez dźwięku.
 * Wyjątek (SPEC §23.39): gdy rodzic włączył zgodę na nagrania dla profilu, zapis KAŻDEJ próby Azure z kroków 1–2 lekcji (bez ręcznego opisu `label`) idzie na serwer razem
 * z plikiem WAV — razem z tym, co usłyszał Azure (`heard`, `scripted`, `alts`, czyli transkrypty). Obok nagrania jedzie też „oryginał z Azure” (SPEC §23.40): pełne surowe
 * odpowiedzi obu rozpoznawań i kontekst (wersja kodu, progi, ustawienia Azure, przeglądarka) — ten zapis próby NIE zawiera ich, są w osobnym pliku. Domyślnie (zgoda wyłączona)
 * nic stąd nie opuszcza przeglądarki.
 */

export const ATTEMPT_LOG_KEY = 'naative.attempts.v1';
export const TEST_MODE_KEY = 'naative.testmode.v1';
export const MAX_ATTEMPTS = 300;

export type AttemptLabel = 'ok' | 'bad';

/** Głoska: [symbol, ocena, początek ms, czas ms] (dwa ostatnie, gdy Azure je podał). */
export type AttemptPhoneme = [string, number, number?, number?];

export interface AttemptWord {
  w: string;
  /** ocena słowa */
  a: number;
  /** rodzaj błędu (None / Omission / Mispronunciation / Insertion) */
  e: string;
  off?: number;
  dur?: number;
  ph?: AttemptPhoneme[];
}

export interface AttemptRecord {
  id: string;
  /** data i godzina próby (ISO) */
  t: string;
  /** zdanie wzorcowe */
  ref: string;
  sid: string;
  src: AssessmentSource;
  /** co usłyszało zwykłe rozpoznawanie (bez wzorca) */
  heard?: string;
  /** co usłyszał Azure, znając wzorzec */
  scripted?: string;
  conf?: number;
  ties?: number;
  /** wersje z NBest: [tekst, pewność] */
  alts?: Array<[string, number]>;
  acc: number;
  comp: number;
  flu?: number;
  onset?: number;
  passed: boolean;
  stars: number;
  reason: string;
  words: AttemptWord[];
  label?: AttemptLabel;
}

/** Minimalny interfejs pamięci (localStorage albo atrapa w testach). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const round1 = (n: number): number => Math.round(n * 10) / 10;

/** `random` tylko dla testów (powtarzalne identyfikatory); id musi być unikalne także dla dwóch prób zrobionych w tej samej milisekundzie. */
export function makeAttemptRecord(a: { now: Date; seq: number; sentenceId: string; reference: string; result: AssessmentResult; verdict: Verdict; random?: () => number }): AttemptRecord {
  const { result: r, verdict: v } = a;
  const rec: AttemptRecord = {
    id: `${a.now.getTime().toString(36)}-${a.seq}-${Math.floor((a.random ?? Math.random)() * 1_679_616).toString(36)}`,
    t: a.now.toISOString(),
    ref: a.reference,
    sid: a.sentenceId,
    src: r.source,
    acc: round1(r.accuracy),
    comp: round1(r.completeness),
    passed: v.passed,
    stars: v.stars,
    reason: v.reason,
    words: r.words.map((w) => {
      const word: AttemptWord = { w: w.word, a: round1(w.accuracy), e: w.error };
      if (w.offsetMs !== undefined) word.off = w.offsetMs;
      if (w.durationMs !== undefined) word.dur = w.durationMs;
      if (w.phonemes?.length) {
        word.ph = w.phonemes.map((p) => {
          const ph: AttemptPhoneme = [p.phoneme, round1(p.accuracy)];
          if (p.offsetMs !== undefined && p.durationMs !== undefined) ph.push(p.offsetMs, p.durationMs);
          return ph;
        });
      }
      return word;
    }),
  };
  if (r.recognizedText !== undefined) rec.heard = r.recognizedText;
  if (r.scriptedText !== undefined) rec.scripted = r.scriptedText;
  if (r.transcriptConfidence !== undefined) rec.conf = Math.round(r.transcriptConfidence * 1000) / 1000;
  if (r.transcriptTies !== undefined) rec.ties = r.transcriptTies;
  if (r.transcriptAlternatives?.length) rec.alts = r.transcriptAlternatives.map((x) => [x.text, Math.round(x.confidence * 1000) / 1000]);
  if (r.fluency !== undefined) rec.flu = round1(r.fluency);
  if (r.speechOnsetMs !== undefined) rec.onset = r.speechOnsetMs;
  return rec;
}

const isRecord = (x: unknown): x is AttemptRecord => {
  if (typeof x !== 'object' || x === null) return false;
  const o = x as Record<string, unknown>;
  return typeof o.id === 'string' && typeof o.t === 'string' && typeof o.ref === 'string' && typeof o.passed === 'boolean' && Array.isArray(o.words);
};

/** Wczytuje dziennik; uszkodzone dane albo brak pamięci = pusta lista (nigdy wyjątek). */
export function loadAttempts(store: StorageLike | null): AttemptRecord[] {
  if (!store) return [];
  try {
    const raw = store.getItem(ATTEMPT_LOG_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isRecord) : [];
  } catch {
    return [];
  }
}

function saveAttempts(store: StorageLike | null, list: AttemptRecord[]): boolean {
  if (!store) return false;
  try {
    store.setItem(ATTEMPT_LOG_KEY, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}

/** Dopisuje próbę; trzyma najwyżej MAX_ATTEMPTS ostatnich, ale próby opisane etykietą wypadają dopiero po nieopisanych. */
export function addAttempt(store: StorageLike | null, rec: AttemptRecord): boolean {
  const list = [...loadAttempts(store), rec];
  while (list.length > MAX_ATTEMPTS) {
    const i = list.findIndex((x) => x.label === undefined);
    list.splice(i >= 0 ? i : 0, 1);
  }
  return saveAttempts(store, list);
}

/** Ustawia (albo zdejmuje, gdy `label` to undefined) opis próby. Zwraca false, gdy próby nie ma albo nie da się zapisać. */
export function labelAttempt(store: StorageLike | null, id: string, label: AttemptLabel | undefined): boolean {
  const list = loadAttempts(store);
  const rec = list.find((x) => x.id === id);
  if (!rec) return false;
  if (label === undefined) delete rec.label;
  else rec.label = label;
  return saveAttempts(store, list);
}

export function clearAttempts(store: StorageLike | null): void {
  try {
    store?.removeItem(ATTEMPT_LOG_KEY);
  } catch {
    /* bez pamięci nie ma czego czyścić */
  }
}

export function isTestMode(store: StorageLike | null): boolean {
  try {
    return store?.getItem(TEST_MODE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setTestMode(store: StorageLike | null, on: boolean): void {
  try {
    if (on) store?.setItem(TEST_MODE_KEY, '1');
    else store?.removeItem(TEST_MODE_KEY);
  } catch {
    /* ignorujemy */
  }
}

export interface AttemptStats {
  total: number;
  labelled: number;
  ok: number;
  bad: number;
}

export function attemptStats(list: readonly AttemptRecord[]): AttemptStats {
  const ok = list.filter((x) => x.label === 'ok').length;
  const bad = list.filter((x) => x.label === 'bad').length;
  return { total: list.length, labelled: ok + bad, ok, bad };
}

/**
 * Tekst do skopiowania i wklejenia w rozmowie: nagłówek i po jednej linii JSON na próbę (tylko opisane, najnowsze `limit`).
 * Linia = jedna próba, więc nawet ucięte wklejenie nie psuje wcześniejszych.
 */
export function exportAttempts(list: readonly AttemptRecord[], limit = 60): string {
  const picked = list.filter((x) => x.label !== undefined).slice(-limit);
  const lines = picked.map((x) => JSON.stringify(x));
  return [`naative-attempts v1 · ${picked.length} opisanych prób`, ...lines].join('\n');
}

/** Dziennik dla aplikacji: wszystkie operacje na jednej pamięci (null = przeglądarka bez dostępu; wtedy nic się nie zapisuje, ale nic się nie psuje). */
export interface AttemptStore {
  record(rec: AttemptRecord): void;
  label(id: string, label: AttemptLabel | undefined): void;
  list(): AttemptRecord[];
  clear(): void;
  testMode(): boolean;
  setTestMode(on: boolean): void;
}

export function createAttemptStore(store: StorageLike | null): AttemptStore {
  return {
    record: (rec) => void addAttempt(store, rec),
    label: (id, label) => void labelAttempt(store, id, label),
    list: () => loadAttempts(store),
    clear: () => clearAttempts(store),
    testMode: () => isTestMode(store),
    setTestMode: (on) => setTestMode(store, on),
  };
}
